'use client';

import { PinIcon } from '@insta-club/components';
import Eye from '@insta-club/components/icons/eye';
import { useBoolean } from '@insta-club/hooks';
import { Note } from '@insta-club/models';
import { BottomSheet } from '@insta-club/ui';
import { cn } from '@insta-club/utilities';
import Image from 'next/image';

interface Props {
    notes: Note[];
    pinnedNotes?: Note[];
}

interface NoteCardProps {
    note: Note;
    isPinned?: boolean;
}

const NoteCard: React.FC<NoteCardProps> = ({ note, isPinned = false }) => {
    const { user, text } = note;
    const userFullName = `${user.first_name} ${user.last_name}`;
    const userImage = user.image ?? '/assets/images/default-profile.png';

    return (
        <div className={cn('rounded-xl p-4', 'bg-gray-100')}>
            <div className='space-y-2'>
                <div className='flex items-center justify-between gap-1'>
                    <div className='flex items-start gap-2'>
                        <Image
                            className='rounded-full object-cover'
                            width={25}
                            height={25}
                            src={userImage}
                            alt={`${userFullName} profile`}
                            loading='lazy'
                        />
                        <div className='flex items-center gap-2'>
                            <h6 className='text-sm font-semibold text-gray-900'>{userFullName}</h6>
                            {user.role && <span className='text-xs font-medium text-gray-500'>( {user.role} )</span>}
                        </div>
                    </div>
                    {isPinned && <PinIcon width={16} className='text-primary-900 -rotate-90' />}
                </div>
                <p className='text-sm leading-6 font-medium text-gray-600'>{text}</p>
            </div>
        </div>
    );
};

const ProgramNote: React.FC<Props> = ({ notes, pinnedNotes = [] }) => {
    const sheet = useBoolean();
    const totalNotes = notes.length + pinnedNotes.length;
    return (
        <>
            <div className='flex items-start justify-between'>
                <div>
                    <h5 className='text-primary-900 text-xl font-bold'>
                        <span>یادداشت</span> <span className='text-primary-500'>منتور</span>
                    </h5>
                    <p className='text-sm text-gray-500'>{totalNotes} پیام جدید</p>
                </div>
                <button
                    onClick={sheet.onTrue}
                    className='flex items-center space-x-1 rounded-full bg-black px-5 py-1 text-xs text-white'
                    aria-label='نمایش یادداشت‌ها'
                >
                    <Eye width={12} />
                    <span>نمایش</span>
                </button>
            </div>

            {totalNotes && (
                <BottomSheet open={sheet.value} onClose={sheet.onFalse} title='یادداشت منتور'>
                    <div className='custom-scrollbar max-h-96 space-y-2 overflow-y-auto px-1'>
                        {pinnedNotes.map((note) => (
                            <NoteCard key={note.id} note={note} isPinned />
                        ))}
                        {notes.map((note) => (
                            <NoteCard key={note.id} note={note} />
                        ))}
                    </div>
                </BottomSheet>
            )}
        </>
    );
};

export default ProgramNote;

